import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Autocomplete,
  Snackbar,
  Alert,
  Chip,
  FormControlLabel,
  Switch,
  Tooltip,
  Menu,
  MenuItem as MMenuItem,
  Checkbox,
  ListItemText,
  TextField as MuiTextField,
  InputAdornment,
  Divider,
  TablePagination,
  OutlinedInput,
  DialogContentText,
  Popover,
  Collapse,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  VisibilityOff as VisibilityOffIcon,
  Visibility as VisibilityIcon,
  Receipt as ReceiptIcon,
  CalendarToday as CalendarTodayIcon,
  LocalShipping as LocalShippingIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';

import { useAuth } from '../context/AuthContext';
// import { db, auth } from '../firebase/config';
// import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, where, increment, Timestamp, orderBy, writeBatch, getDoc } from 'firebase-shim';
import * as indexedDBService from '../utils/indexedDBService';
import { Purchase, PurchaseItem } from '../types/purchase';
import { InventoryItem } from '../types/inventory';
import { Vendor } from '../types/vendor';

// Function to update inventory quantities after purchase received
const updateInventoryAfterPurchase = async (purchaseItems: PurchaseItem[], purchaseId: string, invoiceNumber: string, userId: string) => {
  try {
    const inventoryUpdates: Array<{
      id: string;
      name: string;
      previousQuantity: number;
      newQuantity: number;
    }> = [];

    // Process each purchase item that has an inventory item linked
    for (const item of purchaseItems) {
      // Skip items that don't have an inventory item ID
      if (!item.inventoryItemId) continue;

      // Get the current inventory data
      const inventoryData = await indexedDBService.getItemById<InventoryItem>('inventory', item.inventoryItemId);

      if (inventoryData) {
        // Calculate new stock quantity (add instead of subtract)
        const previousQuantity = Number(inventoryData.stockQuantity) || 0;
        const newQuantity = previousQuantity + (Number(item.quantity) || 0);

        // Update the inventory item with the new quantity
        await indexedDBService.putItem('inventory', {
          ...inventoryData,
          stockQuantity: newQuantity,
          updatedAt: new Date().toISOString()
        });

        // Create history data
        const historyData = {
          inventoryItemId: item.inventoryItemId || '',
          itemName: inventoryData.name || 'Unnamed Item',
          previousQuantity: previousQuantity,
          newQuantity: newQuantity,
          changeAmount: Number(item.quantity) || 0,
          type: 'purchase',
          purchaseId: purchaseId || '',
          invoiceNumber: invoiceNumber || '',
          notes: `Addition from Purchase ${invoiceNumber || ''}`,
          timestamp: new Date().toISOString(),
          userId: userId
        };

        // Add to history store
        const historyId = 'hist_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        await indexedDBService.putItem('inventoryHistory', {
          ...historyData,
          id: historyId
        });

        // Add to updates array
        inventoryUpdates.push({
          id: item.inventoryItemId,
          name: inventoryData.name || 'Unnamed Item',
          previousQuantity: previousQuantity,
          newQuantity: newQuantity
        });
      }
    }

    return inventoryUpdates;
  } catch (error) {
    console.error('Error updating inventory after purchase:', error);
    // Return empty array instead of throwing to prevent cascading errors
    return [];
  }
};

const Purchases: React.FC = () => {
  const { currentUser } = useAuth();
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState<Purchase | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [newPurchase, setNewPurchase] = useState<Partial<Purchase>>({
    vendorId: '',
    invoiceNumber: '',
    date: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    items: [],
    subtotal: 0,
    totalGst: 0,
    totalAmount: 0,
    status: 'draft',
    received: false,
    notes: '',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [receivedFilter, setReceivedFilter] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchAnchorEl, setSearchAnchorEl] = useState<null | HTMLElement>(null);
  const [showCards, setShowCards] = useState(true);
  const [summaryStats, setSummaryStats] = useState({
    totalPurchases: 0,
    monthlyPurchases: 0,
    totalNotReceived: 0,
    totalNotReceivedValue: 0,
    totalDue: 0
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPurchases();
    fetchInventoryItems();
    fetchVendors();
    if (currentUser) fetchVendorPayables();
  }, [currentUser]);

  const fetchPurchases = async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const fetchedPurchases = await indexedDBService.getAllItems<Purchase>('purchases');

      // Sort by createdAt desc
      fetchedPurchases.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA;
      });

      setPurchases(fetchedPurchases);
      calculateSummaryStats(fetchedPurchases);
    } catch (error) {
      console.error('Error fetching purchases:', error);
      setSnackbar({
        open: true,
        message: 'Error fetching purchases',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchInventoryItems = async () => {
    if (!currentUser) return;

    try {
      const itemsData = await indexedDBService.getAllItems<InventoryItem>('inventory');
      setInventoryItems(itemsData);
    } catch (error) {
      console.error('Error fetching inventory items:', error);
    }
  };

  const fetchVendors = async () => {
    if (!currentUser) return;

    try {
      const vendorsData = await indexedDBService.getAllItems<Vendor>('vendors');
      setVendors(vendorsData);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchVendorPayables = async () => {
    if (!currentUser) return;

    try {
      // Get all purchases to calculate payables
      const allPurchases = await indexedDBService.getAllItems<Purchase>('purchases');
      const pendingPurchases = allPurchases.filter(p => p.status === 'pending');

      const payables: { [vendorId: string]: number } = {};

      pendingPurchases.forEach(purchase => {
        const vendorId = purchase.vendorId;
        const amount = purchase.totalAmount || 0;
        const paidAmount = purchase.paidAmount || 0;
        const pendingAmount = amount - paidAmount;

        if (vendorId && pendingAmount > 0) {
          payables[vendorId] = (payables[vendorId] || 0) + pendingAmount;
        }
      });

      // Update each vendor with new pending amount
      // First get current vendor list to ensure we have update targets
      const currentVendors = await indexedDBService.getAllItems<Vendor>('vendors');

      for (const vendor of currentVendors) {
        const calculatedPending = payables[vendor.id] || 0;
        if (vendor.pendingAmount !== calculatedPending) {
          await indexedDBService.putItem('vendors', {
            ...vendor,
            pendingAmount: calculatedPending
          });
        }
      }
    } catch (error) {
      console.error('Error updating vendor payables:', error);
    }
  };

  const handleOpenDialog = (purchase?: Purchase) => {
    if (purchase) {
      setSelectedPurchase(purchase);
      setNewPurchase(purchase);
    } else {
      setSelectedPurchase(null);
      setNewPurchase({
        vendorId: '',
        invoiceNumber: '',
        date: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        items: [],
        subtotal: 0,
        totalGst: 0,
        totalAmount: 0,
        status: 'draft',
        received: false,
        notes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPurchase(null);
    setNewPurchase({
      vendorId: '',
      invoiceNumber: '',
      date: new Date().toISOString().split('T')[0],
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      items: [],
      subtotal: 0,
      totalGst: 0,
      totalAmount: 0,
      status: 'draft',
      received: false,
      notes: '',
    });
  };

  const handleAddItem = () => {
    const newItem: PurchaseItem = {
      id: `item_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      inventoryItemId: '',
      quantity: 1,
      unitPrice: 0,
      gstRate: 0,
      total: 0,
    };

    setNewPurchase(prev => ({
      ...prev,
      items: [...(prev.items || []), newItem]
    }));
  };

  const handleRemoveItem = (itemId: string) => {
    setNewPurchase({
      ...newPurchase,
      items: (newPurchase.items || []).filter(item => item.id !== itemId),
    });
    calculateTotals();
  };

  const handleItemChange = (itemId: string, field: string, value: any) => {
    const updatedItems = (newPurchase.items || []).map(item => {
      if (item.id === itemId) {
        const updatedItem = { ...item, [field]: value };
        if (field === 'inventoryItemId' || field === 'quantity' || field === 'unitPrice' || field === 'gstRate') {
          const quantity = updatedItem.quantity || 0;
          const unitPrice = updatedItem.unitPrice || 0;
          const gstRate = updatedItem.gstRate || 0;
          updatedItem.total = quantity * unitPrice * (1 + gstRate / 100);
        }
        return updatedItem;
      }
      return item;
    });

    setNewPurchase(prev => ({
      ...prev,
      items: updatedItems
    }));

    setTimeout(calculateTotals, 10);
  };

  const calculateTotals = () => {
    const items = newPurchase.items || [];
    const subtotal = items.reduce((sum, item) => {
      const quantity = item.quantity || 0;
      const unitPrice = item.unitPrice || 0;
      return sum + (quantity * unitPrice);
    }, 0);

    const totalGst = items.reduce((sum, item) => {
      const quantity = item.quantity || 0;
      const unitPrice = item.unitPrice || 0;
      const gstRate = item.gstRate || 0;
      return sum + (quantity * unitPrice * gstRate / 100);
    }, 0);

    const totalAmount = subtotal + totalGst;

    setNewPurchase(prev => ({
      ...prev,
      subtotal,
      totalGst,
      totalAmount
    }));
  };

  const handleSavePurchase = async () => {
    if (!currentUser) return;

    try {
      const purchaseData = {
        ...newPurchase,
        userId: currentUser.uid,
        updatedAt: new Date().toISOString(),
        createdAt: selectedPurchase ? selectedPurchase.createdAt : new Date().toISOString(),
        paidAmount: selectedPurchase?.paidAmount || 0,
      };

      let purchaseId = selectedPurchase?.id;
      if (!purchaseId) {
        purchaseId = 'pur_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      }

      // Save purchase
      await indexedDBService.putItem('purchases', {
        ...purchaseData,
        id: purchaseId
      });

      // Update vendor amount if applicable
      if (purchaseData.vendorId) {
        const vendor = await indexedDBService.getItemById<Vendor>('vendors', purchaseData.vendorId);
        if (vendor) {
          const newTotalAmount = purchaseData.totalAmount || 0;
          let amountToAdd = 0;

          // This logic is complex because it tries to difference the amounts.
          // A safer approach (since we recalculate all payables anyway) might be just to let fetchVendorPayables handle it?
          // BUT fetchVendorPayables is called AFTER this. It recalculates everything based on *saved* purchases.
          // So we might technically skip this incremental update if fetchVendorPayables does the full sweep.
          // HOWEVER, for immediate UI feedback or if fetchVendorPayables is slow, incremental might be good.
          // Given I implemented a full sweep in fetchVendorPayables, I can rely on that.
          // But let's keep the logic or just let fetchVendorPayables do the work.
          // Actually, removing this incremental logic avoids race conditions if fetchVendorPayables runs.
          // The original code did incremental updates.
          // Let's rely on fetchVendorPayables completely to avoid duplication and potential errors.
        }
      }

      // Update inventory if purchase is received
      if (purchaseData.received) {
        const invoiceNumber = purchaseData.invoiceNumber || 'No Invoice';
        const purchaseItems = purchaseData.items || [];
        await updateInventoryAfterPurchase(purchaseItems, purchaseId, invoiceNumber, currentUser.uid);
      }

      setSnackbar({
        open: true,
        message: `Purchase ${selectedPurchase ? 'updated' : 'added'} successfully`,
        severity: 'success',
      });
      handleCloseDialog();
      await fetchPurchases();
      await fetchVendorPayables(); // This will correct all vendor balances
    } catch (error) {
      console.error('Error saving purchase:', error);
      setSnackbar({
        open: true,
        message: 'Error saving purchase',
        severity: 'error',
      });
    }
  };

  const handleDeletePurchase = async (purchaseId: string) => {
    try {
      await indexedDBService.deleteItem('purchases', purchaseId);
      setSnackbar({
        open: true,
        message: 'Purchase deleted successfully',
        severity: 'success',
      });
      fetchPurchases();
      fetchVendorPayables();
    } catch (error) {
      console.error('Error deleting purchase:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting purchase',
        severity: 'error',
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const exportToCSV = () => {
    if (purchases.length === 0) return;

    const headers = ['Invoice Number', 'Vendor', 'Date', 'Due Date', 'Amount', 'Status', 'Received'];

    const csvRows = [
      headers.join(','),
      ...purchases.map(purchase => {
        const vendor = vendors.find(v => v.id === purchase.vendorId);
        return [
          purchase.invoiceNumber || 'N/A',
          vendor?.name || 'Unknown',
          new Date(purchase.date).toLocaleDateString(),
          new Date(purchase.dueDate).toLocaleDateString(),
          purchase.totalAmount.toFixed(2),
          purchase.status,
          purchase.received ? 'Yes' : 'No'
        ].join(',');
      })
    ];

    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `purchases_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenFilterMenu = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilterMenu = () => {
    setFilterAnchorEl(null);
  };

  const handleStatusFilterChange = (status: string) => {
    const currentIndex = statusFilter.indexOf(status);
    const newStatusFilter = [...statusFilter];

    if (currentIndex === -1) {
      newStatusFilter.push(status);
    } else {
      newStatusFilter.splice(currentIndex, 1);
    }

    setStatusFilter(newStatusFilter);
  };

  const handleReceivedFilterChange = (value: string | null) => {
    setReceivedFilter(value === receivedFilter ? null : value);
  };

  const filteredPurchases = purchases.filter(purchase => {
    const searchLower = searchQuery.toLowerCase();
    const vendor = vendors.find(v => v.id === purchase.vendorId);
    const searchMatch = !searchQuery ||
      (purchase.invoiceNumber && purchase.invoiceNumber.toLowerCase().includes(searchLower)) ||
      (vendor && vendor.name.toLowerCase().includes(searchLower)) ||
      purchase.status.toLowerCase().includes(searchLower);

    const statusMatch = statusFilter.length === 0 || statusFilter.includes(purchase.status);

    let receivedMatch = true;
    if (receivedFilter === 'received') {
      receivedMatch = purchase.received === true;
    } else if (receivedFilter === 'not-received') {
      receivedMatch = purchase.received === false;
    }

    return searchMatch && statusMatch && receivedMatch;
  });

  const handleSearchClick = (event: React.MouseEvent<HTMLElement>) => {
    setSearchAnchorEl(event.currentTarget);
  };

  const handleSearchClose = () => {
    setSearchAnchorEl(null);
  };

  const calculatePurchaseDetails = (purchase: Purchase) => {
    const taxableValue = purchase.subtotal || 0;
    const taxAmount = purchase.totalGst || 0;
    const paymentMade = purchase.status === 'paid' ? purchase.totalAmount : purchase.paidAmount || 0;
    const balanceDue = purchase.status === 'paid' ? 0 : (purchase.totalAmount - (purchase.paidAmount || 0));

    return {
      taxableValue,
      taxAmount,
      paymentMade,
      balanceDue
    };
  };

  const calculateSummaryStats = (purchasesData: Purchase[]) => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    const stats = {
      totalPurchases: 0,
      monthlyPurchases: 0,
      totalNotReceived: 0,
      totalNotReceivedValue: 0,
      totalDue: 0
    };

    purchasesData.forEach(purchase => {
      // Total purchases
      stats.totalPurchases += purchase.totalAmount;

      // Monthly purchases
      const purchaseDate = new Date(purchase.date);
      if (purchaseDate >= monthStart) {
        stats.monthlyPurchases += purchase.totalAmount;
      }

      // Items not received - count and value
      if (!purchase.received) {
        stats.totalNotReceived += 1;
        stats.totalNotReceivedValue += purchase.totalAmount;
      }

      // Balance due
      if (purchase.status === 'pending') {
        stats.totalDue += (purchase.totalAmount - (purchase.paidAmount || 0));
      }
    });

    setSummaryStats(stats);
  };

  const renderSummaryCards = () => (
    <Collapse in={showCards}>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.15) 0%, rgba(33, 203, 243, 0.05) 100%)',
              border: '1px solid rgba(33, 150, 243, 0.1)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              '&:hover': {
                boxShadow: '0 8px 25px rgba(33, 150, 243, 0.15)',
                transform: 'translateY(-5px)',
              }
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(33, 150, 243, 0.2)',
                  color: '#2196F3',
                  mr: 2
                }}
              >
                <ReceiptIcon fontSize="small" />
              </Box>
              <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                Total Purchases
              </Typography>
            </Box>

            <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#2196F3' }}>
              ₹{summaryStats.totalPurchases.toFixed(2)}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(129, 199, 132, 0.05) 100%)',
              border: '1px solid rgba(76, 175, 80, 0.1)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              '&:hover': {
                boxShadow: '0 8px 25px rgba(76, 175, 80, 0.15)',
                transform: 'translateY(-5px)',
              }
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(76, 175, 80, 0.2)',
                  color: '#4CAF50',
                  mr: 2
                }}
              >
                <CalendarTodayIcon fontSize="small" />
              </Box>
              <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                Monthly Purchases
              </Typography>
            </Box>

            <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#4CAF50' }}>
              ₹{summaryStats.monthlyPurchases.toFixed(2)}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.15) 0%, rgba(255, 183, 77, 0.05) 100%)',
              border: '1px solid rgba(255, 152, 0, 0.1)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              '&:hover': {
                boxShadow: '0 8px 25px rgba(255, 152, 0, 0.15)',
                transform: 'translateY(-5px)',
              }
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 152, 0, 0.2)',
                  color: '#FF9800',
                  mr: 2
                }}
              >
                <LocalShippingIcon fontSize="small" />
              </Box>
              <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                Goods Not Received
              </Typography>
            </Box>

            <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#FF9800' }}>
              ₹{summaryStats.totalNotReceivedValue.toFixed(2)}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: '100%',
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.15) 0%, rgba(229, 115, 115, 0.05) 100%)',
              border: '1px solid rgba(244, 67, 54, 0.1)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              '&:hover': {
                boxShadow: '0 8px 25px rgba(244, 67, 54, 0.15)',
                transform: 'translateY(-5px)',
              }
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(244, 67, 54, 0.2)',
                  color: '#F44336',
                  mr: 2
                }}
              >
                <AccountBalanceIcon fontSize="small" />
              </Box>
              <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                Balance Due
              </Typography>
            </Box>

            <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#F44336' }}>
              ₹{summaryStats.totalDue.toFixed(2)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Collapse>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" fontWeight="bold" sx={{
          background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          Purchase Management
        </Typography>

        <Box display="flex" gap={2}>
          <Tooltip title="Search Purchases">
            <IconButton color="primary" onClick={handleSearchClick}>
              <SearchIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Filter Purchases">
            <IconButton color="primary" onClick={handleOpenFilterMenu}>
              <FilterListIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Export to CSV">
            <IconButton
              color="primary"
              onClick={exportToCSV}
              disabled={purchases.length === 0}
            >
              <FileDownloadIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={showCards ? "Hide Summary Cards" : "Show Summary Cards"}>
            <IconButton onClick={() => setShowCards(!showCards)} color="primary">
              {showCards ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              backgroundImage: 'var(--primary-gradient)',
              fontWeight: 'medium',
            }}
          >
            Add Purchase
          </Button>
        </Box>
      </Box>

      {renderSummaryCards()}

      <Popover
        open={Boolean(searchAnchorEl)}
        anchorEl={searchAnchorEl}
        onClose={handleSearchClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <FormControl variant="outlined" size="small" sx={{ width: '100%' }}>
            <OutlinedInput
              placeholder="Search Purchase..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              }
            />
          </FormControl>
        </Box>
      </Popover>

      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleCloseFilterMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Status
          </Typography>
          <Box display="flex" flexDirection="column">
            {['draft', 'pending', 'paid'].map((status) => (
              <FormControlLabel
                key={status}
                control={
                  <Checkbox
                    checked={statusFilter.indexOf(status) !== -1}
                    onChange={() => handleStatusFilterChange(status)}
                    size="small"
                  />
                }
                label={status.charAt(0).toUpperCase() + status.slice(1)}
              />
            ))}
          </Box>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Received Status
          </Typography>
          <Box display="flex" flexDirection="column">
            <FormControlLabel
              control={
                <Checkbox
                  checked={receivedFilter === 'received'}
                  onChange={() => handleReceivedFilterChange('received')}
                  size="small"
                />
              }
              label="Received"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={receivedFilter === 'not-received'}
                  onChange={() => handleReceivedFilterChange('not-received')}
                  size="small"
                />
              }
              label="Not Received"
            />
          </Box>
        </Box>
      </Menu>

      <TableContainer component={Paper} elevation={0} sx={{
        borderRadius: 3,
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
        overflowX: 'auto',
        width: '100%'
      }}>
        <Table sx={{ minWidth: 1400 }} size="small">
          <TableHead>
            <TableRow sx={{ background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)' }}>
              <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Date</TableCell>
              <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '120px', fontWeight: 'bold' }}>Purchase Invoice No.</TableCell>
              <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '120px', fontWeight: 'bold' }}>GST Number</TableCell>
              <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '150px', fontWeight: 'bold' }}>Vendor Name</TableCell>
              <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '120px', fontWeight: 'bold' }}>Taxable Value</TableCell>
              <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '120px', fontWeight: 'bold' }}>Tax Amount</TableCell>
              <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '120px', fontWeight: 'bold' }}>Invoice Value</TableCell>
              <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '120px', fontWeight: 'bold' }}>Payment Made</TableCell>
              <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '120px', fontWeight: 'bold' }}>Balance Amount due</TableCell>
              <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '120px', fontWeight: 'bold' }}>Goods Received</TableCell>
              <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', minWidth: '100px', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPurchases
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((purchase) => {
                const vendor = vendors.find(v => v.id === purchase.vendorId);
                const { taxableValue, taxAmount, paymentMade, balanceDue } = calculatePurchaseDetails(purchase);

                return (
                  <TableRow key={purchase.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(purchase.date).toLocaleDateString()}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{purchase.invoiceNumber}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{vendor?.gstin || 'N/A'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{vendor?.name}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>₹{taxableValue.toFixed(2)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>₹{taxAmount.toFixed(2)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>₹{purchase.totalAmount.toFixed(2)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>₹{paymentMade.toFixed(2)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {purchase.status === 'paid' ? (
                        <Typography color="success.main" sx={{ whiteSpace: 'nowrap' }}>₹0.00</Typography>
                      ) : (
                        <Typography color="warning.main" sx={{ whiteSpace: 'nowrap' }}>₹{balanceDue.toFixed(2)}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Chip
                        label={purchase.received ? "Received" : "Not Received"}
                        color={purchase.received ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <IconButton onClick={() => handleOpenDialog(purchase)} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton onClick={() => handleDeletePurchase(purchase.id)} size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            {filteredPurchases.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                  <Typography variant="subtitle1" color="text.secondary">
                    No purchases found matching your criteria
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
        component="div"
        count={filteredPurchases.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        sx={{ mt: 2 }}
      />

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedPurchase ? 'Edit Purchase' : 'Add New Purchase'}
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} p={1}>
            <Autocomplete
              key="vendor-select"
              options={vendors}
              getOptionLabel={(option) => option.name}
              value={vendors.find(v => v.id === newPurchase.vendorId) || null}
              onChange={(_, value) => {
                setNewPurchase(prev => ({
                  ...prev,
                  vendorId: value?.id || ''
                }));
              }}
              renderInput={(params) => (
                <TextField {...params} label="Vendor" fullWidth required />
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              openOnFocus
              selectOnFocus
              disablePortal
              filterOptions={(options, state) => {
                const inputValue = state.inputValue.toLowerCase().trim();
                if (!inputValue) return options;
                return options.filter(option =>
                  option.name.toLowerCase().includes(inputValue) ||
                  (option.contactPerson || '').toLowerCase().includes(inputValue)
                );
              }}
            />
            <TextField
              label="Invoice Number"
              value={newPurchase.invoiceNumber}
              onChange={(e) => setNewPurchase({ ...newPurchase, invoiceNumber: e.target.value })}
              fullWidth
            />
            <TextField
              label="Date"
              type="date"
              value={newPurchase.date}
              onChange={(e) => setNewPurchase({ ...newPurchase, date: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Due Date"
              type="date"
              value={newPurchase.dueDate}
              onChange={(e) => setNewPurchase({ ...newPurchase, dueDate: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={newPurchase.received}
                  onChange={(e) => setNewPurchase({ ...newPurchase, received: e.target.checked })}
                  color="primary"
                />
              }
              label="Goods Received"
            />

            <Typography variant="subtitle1" fontWeight="medium">
              Items
            </Typography>

            {(newPurchase.items || []).map((item) => {
              const inventoryItem = inventoryItems.find(i => i.id === item.inventoryItemId);
              return (
                <Box key={item.id} display="flex" gap={2} alignItems="center">
                  <Autocomplete
                    key={`item-select-${item.id}`}
                    options={inventoryItems}
                    getOptionLabel={(option) => `${option.name} (${option.skuWithUnit || ''})`}
                    value={inventoryItem || null}
                    onChange={(event, value) => {
                      if (value) {
                        const updatedItems = (newPurchase.items || []).map(i => {
                          if (i.id === item.id) {
                            return {
                              ...i,
                              inventoryItemId: value.id,
                              gstRate: value.gstRate || 0,
                              unitPrice: i.unitPrice || value.purchasePrice || 0
                            };
                          }
                          return i;
                        });

                        setNewPurchase(prev => ({
                          ...prev,
                          items: updatedItems
                        }));

                        setTimeout(calculateTotals, 10);
                      } else {
                        handleItemChange(item.id, 'inventoryItemId', '');
                      }
                    }}
                    sx={{ flex: 2 }}
                    renderInput={(params) => (
                      <TextField {...params} label="Item" fullWidth required />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    disablePortal
                    openOnFocus
                    selectOnFocus
                    blurOnSelect
                    filterOptions={(options, state) => {
                      const inputValue = state.inputValue.toLowerCase().trim();
                      if (!inputValue) return options;
                      return options.filter(option =>
                        option.name.toLowerCase().includes(inputValue) ||
                        (option.skuWithUnit || '').toLowerCase().includes(inputValue)
                      );
                    }}
                    ListboxProps={{
                      style: { maxHeight: '200px' }
                    }}
                  />
                  <TextField
                    label="Quantity"
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(item.id, 'quantity', Number(e.target.value))}
                    sx={{ flex: 1 }}
                    inputProps={{ min: 1 }}
                  />
                  <TextField
                    label="Unit Price"
                    type="number"
                    value={item.unitPrice}
                    onChange={(e) => handleItemChange(item.id, 'unitPrice', Number(e.target.value))}
                    sx={{ flex: 1 }}
                    inputProps={{ min: 0, step: "0.01" }}
                  />
                  <TextField
                    label="GST Rate"
                    type="number"
                    value={item.gstRate}
                    onChange={(e) => handleItemChange(item.id, 'gstRate', Number(e.target.value))}
                    sx={{ flex: 1 }}
                    inputProps={{ min: 0, step: "0.01" }}
                  />
                  <TextField
                    label="Total"
                    value={item.total.toFixed(2)}
                    InputProps={{ readOnly: true }}
                    sx={{ flex: 1 }}
                  />
                  <IconButton onClick={() => handleRemoveItem(item.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              );
            })}

            <Button
              startIcon={<AddIcon />}
              onClick={handleAddItem}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add Item
            </Button>

            <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
              <Typography variant="subtitle1">
                Subtotal: ₹{newPurchase.subtotal?.toFixed(2)}
              </Typography>
              <Typography variant="subtitle1">
                GST: ₹{newPurchase.totalGst?.toFixed(2)}
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold">
                Total: ₹{newPurchase.totalAmount?.toFixed(2)}
              </Typography>
            </Box>

            <TextField
              label="Notes"
              value={newPurchase.notes}
              onChange={(e) => setNewPurchase({ ...newPurchase, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={newPurchase.status}
                label="Status"
                onChange={(e) => setNewPurchase({ ...newPurchase, status: e.target.value as any })}
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSavePurchase}
            variant="contained"
            sx={{
              backgroundImage: 'var(--primary-gradient)',
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Purchases; 
